import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useSession } from "../shell/session";
import { BrandFoot, BrandLockup, useBrand } from "../shell/brand";
import { useHostBrand } from "../shell/hostbrand";
import { CALLBACK_PATH, beginSignIn, browser, finishSignIn, type SignInProvider } from "../shell/provider";
import { forgetAddress, rememberedAddress } from "../shell/remembered";
import { IDENTITY_LINK_PATH, handOffClaim } from "./IdentityLink";
import { Loading, ProblemBox } from "../ui";
import { discoverDoor, useSignInDoors } from "./doors";
import { ACCEPT_PATH } from "./Accept";
import { FORGOT_PASSWORD_PATH } from "./PasswordReset";
import type { Problem } from "../api/problem";

// Restates ADR-0103 decision 3, ZENITH.md §6.3 — one refusal for every way the exchange can
// refuse, naming no customer and confirming no account.
/** The one sentence every refused or unfinished provider sign-in gets (#820;
 * ZENITH.md §6.3, §19.2). The exchange refuses a token that did not verify, a
 * person nobody invited and an organization no enabled method covers with one
 * answer, and a return that could not finish — cancelled at the provider, a
 * state that did not match — is folded in with them: it names no customer,
 * confirms no account, and cannot tell a known person from an unknown one. */
export const PROVIDER_REFUSED = "That sign-in was not accepted. Try again, or sign in with your email and password.";

/** The provider did not answer when the person asked to go to it: an outage,
 * about nobody. */
export const PROVIDER_UNAVAILABLE = "Your organization's sign-in is not answering right now. Try again, or sign in with your email and password.";

/* The provider this deployment names is read by `useSignInDoors` (./doors),
 * which shares its read with the invitation's acceptance page (#506). The password form is
 * drawn either way, so a slow or failed read leaves today's page and never an
 * empty one (#820). */

export default function Login() {
  const { signIn, exchange, refresh } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  // `discovery` is the neutral home's address step (ADR-0183 decision 2f):
  // the email field comes first and the provider door reads it. A bound
  // host's page is the one it was.
  const { provider, discovery, passwordReset } = useSignInDoors();
  // The brand this host wears, resolved by the server from the host the
  // request arrived on (ZENITH.md §18, ADR-0065; #505). An organization's
  // brand is worn; the neutral platform home resolves to the built-in
  // default — `source: "default"` — and this page stays unmarked there, which
  // is decision 3. An unbound host never reaches this page at all: the server
  // refuses it before the bundle is served (decision 8).
  const host = useHostBrand();
  const branded = host && host.source === "organization" ? host : undefined;
  // The document wears it too — title, favicon, accent tokens — for the
  // reason #253 applies the theme ahead of this gate: a person should meet
  // the brand, not watch it arrive.
  //
  // **It is applied here rather than in `App`, and that is not a style
  // choice.** `App` sits above `Shell`, and React runs a child's effects
  // before its parent's — so a host brand applied in `App` would run its
  // `applyBrand(undefined)` cleanup *after* `Shell` had already put
  // `Me.brand` on the document, and wipe it. This component is rendered only
  // while nobody is signed in, so its unmount is exactly the moment the host
  // brand should come off, and it comes off before the shell's goes on.
  useBrand(branded, "host");
  // The address the last session in this browser held, kept until Sign out or
  // Switch user (#2054). Where there is one the page opens on it, and nobody
  // types it again after their session ended by itself.
  const [remembered, setRemembered] = useState(rememberedAddress);
  const [email, setEmail] = useState(remembered ?? "");
  // Switch user asks the provider for a fresh sign-in on the next door, since
  // the previous person's session there outlives Zenith's (#2054, ruled
  // 2026-10-27).
  const [switched, setSwitched] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  // A provider sign-in's outcome, in the page's own words rather than the
  // server's, so every refusal reads the same whatever the server said.
  const [notice, setNotice] = useState<string>();
  const returning = location.pathname === CALLBACK_PATH;
  const [finishing, setFinishing] = useState(returning);
  // The page is one form and the field is where the visitor is headed; a
  // ref rather than `autoFocus`, which jsx-a11y refuses for the general case.
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!finishing) first.current?.focus(); }, [finishing, remembered]);

  // The return from the provider. Run once: the remembered state is consumed
  // as it is read, and React's development double effect must not read it a
  // second time and report a refusal the first read did not have.
  const finished = useRef(false);
  useEffect(() => {
    if (!returning || finished.current) return;
    finished.current = true;
    void (async () => {
      const got = await finishSignIn(location.search);
      // **A person spending an identity link is not signing in** (ADR-0182;
      // #1034): the link binds a sign-in and opens no session, so the
      // exchange below would refuse the very account being linked. Its page
      // finishes it, handed the tokens in memory.
      if (got?.intent === "claim" && got.claim) {
        handOffClaim(got.idToken, got.claim);
        navigate(IDENTITY_LINK_PATH, { replace: true });
        return;
      }
      // **An invitee returning from the provider is accepting, not signing
      // in** (#506, ADR-0142 decision 2). They hold no membership yet, so the
      // exchange below would refuse them — the acceptance is what creates the
      // membership, and it starts the session itself. The callback address is
      // one, so the two are told apart by where the person was headed.
      if (got && got.returnTo.startsWith(ACCEPT_PATH)) {
        const token = new URLSearchParams(got.returnTo.split("?")[1] ?? "").get("token") ?? "";
        const { response } = await api.POST("/invitations:accept", { body: { token, id_token: got.idToken } });
        if (response.ok) {
          navigate("/", { replace: true });
          await refresh();
          return;
        }
        // Back to the acceptance page with its own refusal, which says what
        // an invitee can do about it. The token stays in the address because
        // it is where it arrived; nothing new is disclosed by it.
        navigate(got.returnTo, { replace: true, state: { refused: true } });
        setFinishing(false);
        return;
      }
      if (got && await exchange(got.idToken)) {
        // Off the callback address before the session lands, so the shell
        // opens where the person was headed and never draws this address.
        navigate(got.returnTo, { replace: true });
        await refresh();
        return;
      }
      navigate("/", { replace: true });
      setNotice(PROVIDER_REFUSED);
      setFinishing(false);
    })();
  }, [returning, location.search, exchange, refresh, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setProblem(undefined); setNotice(undefined);
    const p = await signIn(email, password);
    setBusy(false);
    if (p) setProblem(p);
  };
  const toProvider = async (p: SignInProvider) => {
    setBusy(true); setProblem(undefined); setNotice(undefined);
    try {
      // On the neutral home the address chooses the door: a person hosted at
      // the provider is sent to the organization that hosts them, anybody
      // else to the door this page already holds.
      const door = discovery ? await discoverDoor(email) : p;
      if (!door) throw new Error("no door");
      browser.assign(await beginSignIn(door, location.pathname + location.search, "sign-in", undefined, switched));
    } catch {
      setNotice(PROVIDER_UNAVAILABLE);
      setBusy(false);
    }
  };

  const switchUser = () => {
    forgetAddress();
    setRemembered(undefined);
    setEmail("");
    setSwitched(true);
    setProblem(undefined); setNotice(undefined);
  };

  // One address field: on the neutral home it sits above the provider door,
  // which reads it, and the password form below uses the same value. Where the
  // browser remembers an address it is shown instead, with the way back to the
  // empty field.
  const emailField = remembered ? (
    <div className="gate-account">
      <p className="gate-account-who"><span className="muted">Signing in as</span> <strong>{remembered}</strong></p>
      <button type="button" className="btn btn-quiet btn-sm" onClick={switchUser}>Switch user</button>
    </div>
  ) : (
    <div className="field">
      <label htmlFor="email">Email</label>
      <input ref={first} id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
    </div>
  );

  if (finishing) return <main className="gate"><Loading label="Signing you in" /></main>;
  return (
    <main className="gate">
      <form className="gate-card form" onSubmit={submit}>
        {/* The mark is the host's, not the person's: nobody has signed in yet,
            so there are no memberships to resolve (ZENITH.md §18, ADR-0065).
            On the neutral platform home there is no mark at all — the product's
            own name is not what a reseller's people should meet first (#389) —
            and that is what `branded` being undefined draws. */}
        {branded && <div className="gate-brand"><BrandLockup brand={branded} source="host" /></div>}
        <div>
          <h1>Sign in</h1>
          {/* With no provider the page is the one it was before a provider could be named (#820):
              the sentence below changes only when there is a second door to name. Its words are
              #1678's, which took "manage your estate" out; that a provider's absence changes
              nothing here is unchanged. */}
          {/* Restates ADR-0103 decision 4 — both doors stand until the password's last lot. */}
          <p className="muted">{provider && discovery
            ? remembered
              ? "Sign in through your organization or with your password."
              : "Enter your email, then sign in through your organization or with your password."
            : provider
              ? "Sign in through your organization, or with your email and password."
              : "Sign in with your email and password."}</p>
        </div>
        {notice && <div role="alert" className="problem"><div><strong>{notice}</strong></div></div>}
        {problem && <ProblemBox problem={problem} />}
        {provider && discovery && (
          <>
            {emailField}
            <button type="button" className="btn btn-primary gate-provider" disabled={busy} onClick={() => void toProvider(provider)}>
              Sign in with your organization
            </button>
            <p className="gate-or muted" aria-hidden="true">or</p>
          </>
        )}
        {provider && !discovery && (
          <>
            <button type="button" className="btn btn-primary gate-provider" disabled={busy} onClick={() => void toProvider(provider)}>
              Sign in with your organization
            </button>
            <p className="gate-or muted" aria-hidden="true">or</p>
          </>
        )}
        {!(provider && discovery) && emailField}
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="actions">
          <button type="submit" className={provider ? "btn" : "btn btn-primary"} disabled={busy}>Sign in</button>
        </div>
        {/* Offered only where the server says a forgotten password is reset by
            mail here: a deployment naming no identity provider (ADR-0466
            decision 4b). */}
        {passwordReset && <p className="muted"><Link to={FORGOT_PASSWORD_PATH}>Forgot your password?</Link></p>}
        {/* The support contact, the legal footer and the "powered by" line, which
            ADR-0065 decision 7 rules are part of what a bound host shows an
            anonymous caller. Drawn by the same component the signed-in shell
            uses, so the two cannot describe a brand differently. */}
        {branded && <BrandFoot brand={branded} />}
      </form>
    </main>
  );
}
